import { useExports } from "api/exports";
import { hasModifyAccessToResource } from "api/general";
import { useReward } from "api/rewards";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { parseFiltersForExport } from "components/Layout/Export/helpers";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import {
  ModalMode,
  RewardRedemptionModals,
} from "components/Tooltips/RewardRedemptionModals";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REWARD_FULFILLMENT_LIST_ID } from "state/tables/types";
import { ExportType } from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { Coupon, RedeemedReward } from "types/rewards";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { abbreviateNumber } from "utilities/numbers";
import { filterLabels } from "./helpers";

export function getRewardType(item: RedeemedReward) {
  if (item.issuedCoupon?.valueType === "percentage") {
    return "percentageCoupon";
  } else {
    return item.rewardType;
  }
}

function CreateItems(items: RedeemedReward[]): Row[] {
  const { message } = useMessage();
  return items.map((item: RedeemedReward) => ({
    cells: [
      { content: item.issuedRewardId, property: "uniqueIdentifier" },
      { content: OLDate(item.redemptionDate), property: "redemptionDate" },
      {
        content: OLDate(item.issuedCoupon?.activeTo),
        property: "issuedCoupon:activeTo",
      },
      { content: item.name, property: "name" },
      { content: message({ id: getRewardType(item) }), property: "rewardType" },
      {
        content: item.customerData?.firstName,
        property: "customerData:firstName",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.lastName,
        property: "customerData:lastName",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.email,
        property: "customerData:email",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.phone,
        property: "customerData:phone",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.loyaltyCardNumber,
        property: "customerData:loyaltyCardNumber",
        dataHjSuppress: true,
      },
      { content: message({ id: item.status }), property: "status" },
    ],
    id: item.issuedRewardId,
    metadata: {
      issuedCoupon: item.issuedCoupon,
      memberId: item.customerId,
      rewardId: item.rewardId,
    },
  }));
}

export function RewardFulfillmentList() {
  const { message } = useMessage();
  const { listing, updateListing } = useListing(REWARD_FULFILLMENT_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[REWARD_FULFILLMENT_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const setSelectedFilters = (
    filters: React.SetStateAction<SelectedFilterV2[]>,
  ) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      REWARD_FULFILLMENT_LIST_ID,
    );

    setFilters(filters);
  };
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const hasModifyExportPermission = hasModifyAccessToResource("EXPORT");
  const { useRewardsFulfillmentList } = useReward();
  const { notify } = useNotifications();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    issuedCoupon?: Coupon;
    issuedReward?: string;
    memberId?: string;
    mode: number;
  }>();
  const navigate = useNavigate();
  const { exportList } = useExports();

  const { data, error } = useRewardsFulfillmentList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "redemptionDate",
    dynamicSearch,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "redeemedRewardId" }),
      hasMinWidthThreshold: true,
      property: "uniqueIdentifier",
      isSortable: false,
    },
    {
      headerText: message({ id: "redemptionDate" }),
      property: "redemptionDate",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "activeTo" }),
      property: "issuedCoupon:activeTo",
      isSortable: true,
    },
    {
      headerText: message({ id: "rewardName" }),
      property: "name",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "rewardType" }),
      property: "rewardType",
      isSortable: true,
    },
    {
      headerText: message({ id: "firstName" }),
      property: "customerData:firstName",
      isSortable: true,
    },
    {
      headerText: message({ id: "lastName" }),
      property: "customerData:lastName",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "customerData:email",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "customerData:phone",
      isSortable: true,
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "customerData:loyaltyCardNumber",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: true,
    },
  ]);
  const items = data?.items ? CreateItems(data.items) : [];

  const handleExportList = async () => {
    const filtersObject = dynamicSearch?.value
      ? parseFiltersForExport([
          ...filters,
          {
            // Add dynamicSearch condition at the end to override same condition in filters, "like" condition only
            ...dynamicSearch,
            condition: "like",
            type: "dynamic-search-like",
          },
        ])
      : parseFiltersForExport(filters);

    const data = await exportList(ExportType.REWARD_FULFILLMENT, {
      rewardFulfillment: {
        _orderBy: { createdAt: "desc" },
        _page: listing.page,
        _itemsOnPage: listing.rowsPerPage,
        ...filtersObject,
      },
    });

    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();

            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setConfirmationModalOpen(false);
    }
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, REWARD_FULFILLMENT_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  return (
    <>
      <TopHeader title={message({ id: "rewardsFulfillment" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "rewards" })}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "costInPoints",
              label: message(
                { id: filterLabels.costInPoints },
                { units: defaultWallet?.unitPluralName },
              ),
              type: "number",
            },
            {
              query: "categories",
              label: message({ id: "categories" }),
              type: "reward-categories",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "customerData:email",
              label: message({ id: filterLabels["customerData:email"] }),
              type: "member-email-filter",
            },
            {
              query: "customerData:firstName",
              label: message({ id: filterLabels["customerData:firstName"] }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "customerData:lastName",
              label: message({ id: filterLabels["customerData:lastName"] }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "customerData:loyaltyCardNumber",
              label: message({
                id: "loyaltyCardNumber",
              }),
              type: "member-loyalty-card-number",
              dataHjSuppress: true,
            },
            {
              query: "customerData:phone",
              label: message({ id: filterLabels["customerData:phone"] }),
              type: "member-phone-filter",
              dataHjSuppress: true,
            },
            {
              query: "customerId",
              label: message({ id: filterLabels.customerId }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "rewardType",
              label: message({ id: filterLabels.rewardType }),
              type: "reward-types-filter_eq",
            },
            {
              query: "redemptionDate",
              label: message({ id: filterLabels.redemptionDate }),
              type: "datetime",
            },
            {
              query: "rewardId",
              label: message({ id: filterLabels.rewardId }),
              type: "text",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "reward-name-filter",
            },
            {
              query: "status",
              label: message({ id: filterLabels.status }),
              type: "reward-fulfillment-status",
            },
            {
              query: "token",
              label: message({ id: filterLabels.code }),
              type: "text",
            },
          ]}
          setFilters={setSelectedFilters}
          total={data?.total}
        />
        <SubheaderWrapper>
          {hasModifyExportPermission ? (
            <SubheaderActionButton
              text={message(
                { id: "exportToCSVFile" },
                { value: abbreviateNumber(data?.total.filtered) },
              ).toUpperCase()}
              onClick={() => setConfirmationModalOpen(true)}
              data-test-id="reward-fulfillment-export-to-csv-button"
            />
          ) : null}
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            properties={[
              { id: "name", label: "rewardName" },
              {
                id: "customerData:loyaltyCardNumber",
                label: "loyaltyCardNumber",
              },
              {
                id: "customerData:email",
                label: "email",
              },
              {
                id: "customerData:firstName",
                label: "firstName",
              },
              {
                id: "customerData:lastName",
                label: "lastName",
              },
              {
                id: "customerData:phone",
                label: "phone",
              },
            ]}
            onSearch={() =>
              appDispatch(
                clearTablePage({ tableName: REWARD_FULFILLMENT_LIST_ID }),
              )
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REWARD_FULFILLMENT_LIST_ID}
        data-test-id="reward-fulfillment"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpen(true);
                setModal({
                  issuedReward: id as string,
                  mode: ModalMode.ChangeStatus,
                });
              },
              label: message({ id: "changeStatus" }),
            },
            {
              action: (_, item?: Row) => {
                navigate(links.singleMemberRewards(item?.metadata?.memberId));
              },
              hideCondition: (item: Row) => !item?.metadata?.memberId,
              label: message({ id: "seeMemberDetails" }),
            },
            {
              action: (_, item?: Row) => {
                navigate(links.reward(item?.metadata?.rewardId));
              },
              hideCondition: (item: Row) => !item?.metadata?.rewardId,
              label: message({ id: "seeRewardDetails" }),
            },
            {
              label: message({ id: "markAsUnused" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.ReissueAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !item.metadata.issuedCoupon.usedAt,
            },
            {
              label: message({ id: "markAsUsed" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.RedeemAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !!item.metadata.issuedCoupon.usedAt,
            },
          ],
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "redemptionDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        onRowClick={(id: string) => {
          navigate(links.rewardsFulfillment(id as string));
        }}
      />
      <RewardRedemptionModals
        close={() => setOpen(false)}
        open={open}
        selectedModal={selectedModal}
      />
      <ConfirmationModal
        action={() => handleExportList()}
        close={() => setConfirmationModalOpen(false)}
        header="exportConfirmation"
        info=""
        open={isConfirmationModalOpen}
        cancelText={message({ id: "cancel" })}
        submitText="export"
        data-test-id="export-modal"
        modalWidth={454}
      />
    </>
  );
}
